import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './theme/fonts.css';
import './theme/scrollbar.css';
import './theme/bottom-sheet.css';
import './theme/deck-guide.css';
import { ChakraProvider } from '@chakra-ui/react';
import { ThemeProvider } from 'next-themes';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import App from './App';
import { system } from './theme';
import { COLOR_SCHEME_STORAGE_KEY, installColorScheme } from './theme/colorSchemes';
import { WindowOverlayScroll } from './components/ui/overlay-scroll';
import { RiftboundToaster } from './components/ui/toaster';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PwaUpdateBanner } from './components/PwaUpdateBanner';
import {
  clearAutoRecoverAttempt,
  consumePendingSwResetReload,
  isStaleDeployError,
  recoverFromStaleDeploy,
} from './utils/pwaReload';

/** After a new deploy, allow one automatic reload if cached JS still points at removed chunks. */
function syncDeploySession(): void {
  try {
    const key = 'riftbound.deploy-id';
    const prev = sessionStorage.getItem(key);
    const next = __APP_BUILD_ID__;
    if (prev != null && prev !== next) {
      clearAutoRecoverAttempt();
    }
    sessionStorage.setItem(key, next);
  } catch {
    /* sessionStorage unavailable */
  }
}

function installStaleChunkRecovery(): void {
  window.addEventListener('unhandledrejection', (event) => {
    if (!isStaleDeployError(event.reason)) return;
    event.preventDefault();
    void recoverFromStaleDeploy();
  });
}

installColorScheme();

if (consumePendingSwResetReload()) {
  // Unregister only takes effect on the next navigation — don't mount React.
} else {
  syncDeploySession();
  installStaleChunkRecovery();

  // React.StrictMode is intentionally omitted. Zag.js (used by Chakra UI v3) initialises
  // its state machines via queueMicrotask inside useSafeLayoutEffect. StrictMode's
  // synchronous effect cleanup + remount fires before that microtask, leaving the
  // "open" state effects (scroll-lock, pointer-events: none, focus-trap) registered in
  // a Map that the cleanup closure no longer owns — so those body styles are never
  // restored after a dialog closes. This is a known Zag.js + StrictMode incompatibility.
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <ChakraProvider value={system}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        storageKey={COLOR_SCHEME_STORAGE_KEY}
        disableTransitionOnChange
      >
        <ErrorBoundary>
          <HelmetProvider>
            <App />
            <WindowOverlayScroll />
          </HelmetProvider>
          <RiftboundToaster />
          <Analytics />
          <SpeedInsights sampleRate={0.2} />
        </ErrorBoundary>
        {/* Outside the error boundary so a crashed tree can still activate a waiting SW. */}
        <PwaUpdateBanner />
      </ThemeProvider>
    </ChakraProvider>
  );
}
